/**
 * Preset parser — validates *.preset.yaml content against the PresetManifestSchema.
 *
 * Also performs structural validation beyond what zod alone covers:
 *   - Unique item IDs within the preset
 *   - Cycle detection for nested preset refs (max depth 3)
 *   - Cross-item refs (#item-id) resolve to declared items
 *
 * @docLink packages/library/concepts#preset-parsing
 */

import { type PresetManifest, PresetManifestSchema } from "@skaile/workspaces/types/manifests";
import { parse as yamlParse } from "yaml";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Result of parsing and validating a preset manifest.
 *
 * When `ok` is false, `errors` contains human-readable messages.
 * `preset` may still be set on partial success (structural errors after Zod passes).
 *
 * @docLink packages/library/concepts#preset-parsing
 */
export interface ParseResult {
  /** Whether the preset passed all validation checks. */
  ok: boolean;
  /** The validated preset manifest (set even on structural errors after Zod passes). */
  preset?: PresetManifest;
  /** Validation error messages. */
  errors: string[];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse and validate a preset manifest object.
 *
 * @param data - Raw parsed YAML content (from `yaml` or similar).
 * @returns ParseResult with validated preset or error messages.
 * @docLink packages/library/concepts#preset-parsing
 */
export function parsePreset(data: unknown): ParseResult {
  // Step 1: Zod schema validation
  const zodResult = PresetManifestSchema.safeParse(data);
  if (!zodResult.success) {
    return {
      ok: false,
      errors: zodResult.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
    };
  }

  const preset = zodResult.data;
  const errors: string[] = [];

  // Step 2: Unique item IDs + ambiguous ref detection
  const ids = validateUniqueIds(preset, errors);

  // Step 3: Cross-item refs (#item-id) reference declared IDs
  validateCrossItemRefs(preset, ids, errors);

  // Step 4: Placeholder validation per item
  validatePlaceholderItems(preset, errors);

  if (errors.length > 0) {
    return { ok: false, preset, errors };
  }

  return { ok: true, preset, errors: [] };
}

/**
 * Validate unique item IDs and detect ambiguous shared refs. Pushes any
 * violations onto `errors` and returns the set of declared item IDs.
 */
function validateUniqueIds(preset: PresetManifest, errors: string[]): Set<string> {
  const ids = new Set<string>();
  const refCounts = new Map<string, number>();
  for (const item of preset.items) {
    if (item.id) {
      if (ids.has(item.id)) {
        errors.push(`Duplicate item id: "${item.id}"`);
      }
      ids.add(item.id);
    } else {
      // Track items without IDs that share the same ref (ambiguous for upgrade matching)
      refCounts.set(item.ref, (refCounts.get(item.ref) ?? 0) + 1);
    }
  }
  for (const [ref, count] of refCounts) {
    if (count > 1) {
      errors.push(
        `${count} items share ref "${ref}" without distinct "id" fields — upgrade matching will be ambiguous`,
      );
    }
  }
  return ids;
}

/**
 * Validate that every cross-item ref (`#item-id`) resolves to a declared id.
 * Pushes any violations onto `errors`.
 */
function validateCrossItemRefs(preset: PresetManifest, ids: Set<string>, errors: string[]): void {
  for (const item of preset.items) {
    if (item.composes) {
      for (const compose of item.composes) {
        if (compose.ref.startsWith("#")) {
          const targetId = compose.ref.slice(1);
          if (!ids.has(targetId)) {
            errors.push(
              `Cross-item ref "${compose.ref}" in item "${item.id ?? item.ref}" references undeclared id "${targetId}"`,
            );
          }
        }
      }
    }
  }
}

/**
 * Validate per-item placeholders: unique keys and enum types having values.
 * Pushes any violations onto `errors`.
 */
function validatePlaceholderItems(preset: PresetManifest, errors: string[]): void {
  for (const item of preset.items) {
    if (item.placeholders) {
      const keys = new Set<string>();
      for (const ph of item.placeholders) {
        if (keys.has(ph.key)) {
          errors.push(`Duplicate placeholder key "${ph.key}" in item "${item.id ?? item.ref}"`);
        }
        keys.add(ph.key);

        // enum type requires values
        if (ph.type === "enum" && (!ph.values || ph.values.length === 0)) {
          errors.push(`Placeholder "${ph.key}" has type "enum" but no values defined`);
        }
      }
    }
  }
}

/**
 * Parse preset from a raw YAML string.
 *
 * @param yamlContent - Raw YAML string content of a `*.preset.yaml` file.
 * @returns ParseResult — includes a YAML parse error in `errors` if the string is malformed.
 * @docLink packages/library/concepts#preset-parsing
 */
export function parsePresetYaml(yamlContent: string): ParseResult {
  let data: unknown;
  try {
    data = yamlParse(yamlContent);
  } catch (err) {
    return {
      ok: false,
      errors: [`YAML parse error: ${err instanceof Error ? err.message : String(err)}`],
    };
  }
  return parsePreset(data);
}

/**
 * Detect nested preset refs within a preset's items.
 *
 * A ref is classified as a nested preset if it appears in `knownPresetRefs`.
 * Refs starting with `library://` or `#` are always skipped.
 *
 * @param preset - Validated preset manifest.
 * @param knownPresetRefs - Optional set of refs known to be presets.
 * @returns Array of refs that are nested presets.
 * @docLink packages/library/concepts#preset-parsing
 */
export function detectNestedPresetRefs(
  preset: PresetManifest,
  knownPresetRefs?: Set<string>,
): string[] {
  const nested: string[] = [];
  for (const item of preset.items) {
    // Skip library:// and #cross-item refs
    if (item.ref.startsWith("library://") || item.ref.startsWith("#")) continue;

    // Heuristic: if the ref is in the known presets set, it's nested
    if (knownPresetRefs?.has(item.ref)) {
      nested.push(item.ref);
    }
  }
  return nested;
}

/**
 * Validate nesting depth. Max depth is 3.
 *
 * @param presetRef - The ref of the preset being validated.
 * @param currentDepth - Current nesting depth (0 = top-level).
 * @returns Error message string if depth exceeded, undefined otherwise.
 * @docLink packages/library/concepts#preset-parsing
 */
export function validateNestingDepth(presetRef: string, currentDepth: number): string | undefined {
  const MAX_DEPTH = 3;
  if (currentDepth >= MAX_DEPTH) {
    return `Preset "${presetRef}" exceeds maximum nesting depth (${MAX_DEPTH} levels allowed, depth ${currentDepth} reached)`;
  }
  return undefined;
}
